'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { interactionResponse } from '@zillow/yield-callback';

import { GPT_SCRIPT_ORIGIN, GPT_SCRIPT_SRC } from '@/src/lib/ads/constants';
import { runWhenIdle } from '@/src/lib/utils/runWhenIdle';
import { matchesDesktopViewport, useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';

// Caps how long a session that never interacts waits before GPT loads. Keeps
// the GAM request well ahead of the window `load` event whose lateness cost
// lazyOnload sessions their ad requests entirely (HP-8727).
export const GPT_ACTIVATION_IDLE_TIMEOUT_MS = 3500;

const ACTIVATION_EVENTS = ['pointerdown', 'scroll', 'keydown'] as const;

/**
 * Loads gpt.js as the SRP becomes interactive on desktop, but on mobile waits
 * for the first interaction or browser idle with a capped deadline: gpt.js's
 * ~220ms mobile parse/exec otherwise lands inside the first-tap window and
 * shows up as INP input delay (HP-8776).
 */
export function SrpAdsScript() {
  const isDesktop = useIsDesktop();
  // Seeded synchronously so a desktop cold load mounts the script in the
  // hydration commit itself — `useIsDesktop` reports mobile until it settles,
  // which would otherwise delay desktop GPT discovery by a render. Script
  // renders no DOM, so the server/client difference cannot mismatch markup.
  const [activated, setActivated] = useState(() => matchesDesktopViewport());
  const showScript = isDesktop || activated;

  useEffect(() => {
    if (showScript) return;
    let cancelled = false;
    // pointerdown/keydown BEGIN an interaction — mounting GPT right away could
    // land a fast/cached gpt.js parse inside that same tap's INP window, so
    // wait for the interaction to paint before activating.
    const activate = () => {
      // Promise.resolve tolerates the repo-wide vitest mock, which returns undefined.
      void Promise.resolve(interactionResponse()).then(() => {
        if (!cancelled) setActivated(true);
      });
    };
    const cancelIdle = runWhenIdle(activate, { timeoutMs: GPT_ACTIVATION_IDLE_TIMEOUT_MS });
    ACTIVATION_EVENTS.forEach((event) => window.addEventListener(event, activate, { once: true, passive: true }));
    return () => {
      cancelled = true;
      cancelIdle();
      ACTIVATION_EVENTS.forEach((event) => window.removeEventListener(event, activate));
    };
  }, [showScript]);

  return (
    <>
      {/* Not a preconnect: the preconnect budget in `firstPartyHosts` is spent
          on the LCP image hosts, and browsers act on those in order. */}
      <link rel="dns-prefetch" href={GPT_SCRIPT_ORIGIN} />
      {showScript && <Script id="google-publisher-tags" strategy="afterInteractive" src={GPT_SCRIPT_SRC} />}
    </>
  );
}
